import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  signOut,
  fetchAuthSession,
  type AuthUser,
  type AuthSession,
} from 'aws-amplify/auth';

// Types for the user context
interface UserAttributes {
  identities?: any;
  [key: string]: any;
}

interface UserContextType {
  user: AuthUser | null;
  attributes: UserAttributes | null;
  session: AuthSession | null;
  givenName: string;
  isLoading: boolean;
  handleSignOut: () => Promise<void>;
}

// Create the UserContext
const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [attributes, setAttributes] = useState<UserAttributes | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [givenName, setGivenName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        console.log('Current user:', currentUser);
        console.log('Username:', currentUser.username);

        const currentAttributes = await fetchUserAttributes();
        console.log('User attributes:', currentAttributes);
        setAttributes(currentAttributes);

        // Fetch the auth session
        const authSession = await fetchAuthSession();
        console.log('Auth session:', authSession);
        setSession(authSession);

        // Extract given name from session tokens
        const customGivenName = authSession?.tokens?.idToken?.payload?.['custom:uid'];
        const givenName = authSession?.tokens?.idToken?.payload?.given_name;
        const name = authSession?.tokens?.idToken?.payload?.name;

        const sessionGivenName =
          (typeof customGivenName === 'string' ? customGivenName : null) ||
          (typeof givenName === 'string' ? givenName : null) ||
          (typeof name === 'string' ? name : null) ||
          currentUser.username ||
          'User';
        setGivenName(sessionGivenName);
        setIsLoading(false);
      } catch (error: any) {
        console.error('Error fetching user:', error);
        if (error.name === 'UserUnAuthenticatedException') {
          console.log('User not authenticated, proceeding to sign in');
          try {
            await signInWithRedirect({
              provider: {
                custom: 'AmazonFederate',
              },
            });
          } catch (signInError) {
            console.error('Error signing in:', signInError);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      }
    };

    fetchUser();
  }, []);

  // Update givenName whenever session changes
  useEffect(() => {
    if (session?.tokens?.idToken?.payload) {
      const customGivenName = session.tokens.idToken.payload['custom:uid'];
      const givenNamePayload = session.tokens.idToken.payload.given_name;
      const namePayload = session.tokens.idToken.payload.name;

      const sessionGivenName =
        (typeof customGivenName === 'string' ? customGivenName : null) ||
        (typeof givenNamePayload === 'string' ? givenNamePayload : null) ||
        (typeof namePayload === 'string' ? namePayload : null) ||
        user?.username ||
        'User';
      setGivenName(sessionGivenName);
    }
  }, [session, user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setSession(null);
      setAttributes(null);
      setGivenName('');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value: UserContextType = {
    user,
    attributes,
    session,
    givenName,
    isLoading,
    handleSignOut,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook for using user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
